<template>
  <div class="pharma-daily-container">
    <!-- 导航栏 -->
    <nav class="main-nav">
      <div class="nav-container">
        <div class="nav-logo">
          <div class="logo-icon">
            <img src="../assets/PR_logo_small.png" alt="DiOrion Logo" style="width: 60px; height: auto;">
          </div>
          <h1 class="text-xl font-bold text-gradient"
            style="font-family: 'Times New Roman', Times, serif; font-size: 30px; color: #ffffff;">
            DiOrion-PharmaDaily
          </h1>
        </div>

        <div class="nav-links">
          <!-- 返回按钮 -->
          <button @click="$router.go(-1)" class="mr-4 text-gray-600 hover:text-primary transition-colors">
            <i class="fas fa-arrow-left"></i>
          </button>
          <button class="nav-link" @click="scrollToSection('weather')"
            :class="activeSection === 'weather' ? 'active' : ''">
            Weather
          </button>
          <button class="nav-link" @click="scrollToSection('stocks')"
            :class="activeSection === 'stocks' ? 'active' : ''">
            Pharma Stock Recap
          </button>
          <button class="nav-link" @click="scrollToSection('news')" :class="activeSection === 'news' ? 'active' : ''">
            Latest Pharma News
          </button>
        </div>
      </div>
    </nav>

    <!-- 简介部分 -->
    <section class="intro-section mb-12 rounded-2xl p-6 md:p-8">
      <div class="intro-content">
        <div class="intro-header">
          <div class="intro-title-group">
            <h2 class="text-[clamp(1.5rem,3vw,2.5rem)] font-bold mb-2 title-wrapper"
              style="font-family: 'Times New Roman', Times, serif; font-size: clamp(2.5rem,5vw,4rem); color: rgb(245 240 245);">
              DiOrion Pharma Daily
            </h2>
            <p class="intro-description">
              Stay updated on the latest stock market trends and pharmaceutical industry news to keep pace with
              cutting-edge developments in biopharmaceuticals.
            </p>
          </div>
          <div class="bg-white rounded-xl p-4 shadow-md update-date-container">
            <p class="text-muted text-sm font-medium">Update: <span>{{ formattedUpdateDate }}</span></p>
          </div>
        </div>
      </div>
    </section>

    <!-- 主要内容区域 -->
    <div class="content-container">
      <div class="tools-grid">
        <!-- 天气信息卡片 - 全宽显示 -->
        <div id="weather" class="weather-card tool-card full-width" ref="weatherSection">
          <div class="card-header">
            <div class="tool-icon weather-icon"><i class="fas fa-cloud-rain"></i></div>
            <h3>Shanghai Weather Today</h3>
          </div>
          <div class="card-content">
            <div class="weather-details">
              <div class="weather-item">
                <i class="fas fa-thermometer-half"></i>
                <div>
                  <p class="label">Temperature</p>
                  <p class="value">{{ weather.temperature }}</p>
                </div>
              </div>
              <div class="weather-item">
                <i class="fas fa-tint"></i>
                <div>
                  <p class="label">Humidity</p>
                  <p class="value">{{ weather.humidity }}</p>
                </div>
              </div>
              <div class="weather-item">
                <i class="fas fa-cloud-showers-heavy"></i>
                <div>
                  <p class="label">Condition</p>
                  <p class="value">{{ weather.condition }}</p>
                </div>
              </div>
              <div class="weather-tip">
                <i class="fas fa-lightbulb"></i>
                <p>Tip: {{ weather.tip }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 股票市场回顾 - 全宽显示 -->
        <div id="stocks" class="stock-card tool-card full-width" ref="stockSection">
          <div class="card-header">
            <div class="tool-icon stock-icon"><i class="fas fa-chart-line"></i></div>
            <h3>Pharmaceutical Stock Market Recap</h3>
          </div>
          <div class="card-content">
            <div class="stock-table-container">
              <table class="stock-table">
                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>Closing Price</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(stock, index) in displayedStocks" :key="index" class="stock-row">
                    <td>{{ stock.company }}</td>
                    <td>{{ stock.price }}</td>
                    <td>
                      <span :class="getChangeClass(stock)">
                        {{ stock.change }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- 替换为分页控件 -->
            <div class="pagination-container" v-if="totalStockPages > 1">
              <button class="pagination-btn" @click="prevPage" :disabled="currentPage === 1">
                <i class="fas fa-chevron-left"></i>
              </button>
              <button v-for="page in pageNumbers" :key="page" class="pagination-btn" @click="goToPage(page)"
                :class="{ 'active': page === currentPage }">{{ page }}</button>
              <button class="pagination-btn" @click="nextPage" :disabled="currentPage === totalStockPages">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>

          </div>
        </div>

        <!-- 行业新闻 - 全宽卡片 -->
        <div id="news" class="news-card tool-card full-width" ref="newsSection">
          <div class="card-header">
            <div class="tool-icon news-icon"><i class="fas fa-newspaper"></i></div>
            <h3>Industry News</h3>
          </div>
          <div class="card-content">
            <!-- 新闻分类标签 -->
            <div class="news-tabs">
              <button v-for="(tab, index) in newsTabs" :key="index"
                :class="['tab-btn', activeTab === index ? 'active' : '']" @click="activeTab = index">
                {{ tab }}
              </button>
            </div>

            <!-- 日期筛选标签 -->
            <div class="date-tabs">
              <button v-for="(dateFilter, index) in dateFilters" :key="index"
                :class="['tab-btn', activeDateFilter === index ? 'active' : '']" @click="activeDateFilter = index">
                {{ dateFilter.label }}
              </button>
            </div>

            <!-- 新闻卡片网格 -->
            <div class="news-cards-grid">
              <div v-for="(news, index) in displayedNews" :key="index" class="news-card-item">
                <div class="news-category">{{ news.Category }}</div>
                <h4 class="news-title">{{ news.Title }}</h4>
                <div class="news-meta">
                  <span class="news-date">{{ news.PubData }}</span>
                  <a :href="news.URL" class="read-more" target="_blank" rel="noopener noreferrer">
                    Read More <i class="fas fa-arrow-right"></i>
                  </a>
                </div>
              </div>
            </div>

            <!-- 替换为新闻分页控件 -->
            <div class="pagination-container" v-if="totalNewsPages > 1">
              <button class="pagination-btn" @click="prevNewsPage" :disabled="currentNewsPage === 1">
                <i class="fas fa-chevron-left"></i>
              </button>
              <button v-for="page in newsPageNumbers" :key="page" class="pagination-btn" @click="goToNewsPage(page)"
                :class="{ 'active': page === currentNewsPage }">{{ page }}</button>
              <button class="pagination-btn" @click="nextNewsPage" :disabled="currentNewsPage === totalNewsPages">
                <i class="fas fa-chevron-right"></i>
              </button>

              <!-- 添加页码输入跳转 -->
              <span class="page-jump">
                <input type="number" v-model="jumpToPage" min="1" :max="totalNewsPages" class="page-input">
                <button class="jump-btn" @click="jumpToNewsPage">Go</button>
              </span>
              
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      activeSection: 'weather',
      updateDate: '',
      // 股票数据
      stocks: [],
      newsItems: [],
      weather: {
        temperature: '',
        humidity: '',
        condition: '',
        tip: ''
      },
      // 添加页码输入绑定变量
      jumpToPage: 1,
      // 新闻标签
      newsTabs: [
        'All', 'Company News', 'FDA Approvals', 'Clinical Trials',
        'Regulatory Policies', 'Finance and Market', 'Others'
      ],
      activeTab: 0,
      // 日期筛选
      dateFilters: [
        { label: 'All Dates', value: 'all' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: 'This Week', value: 'week' },
        { label: 'This Month', value: 'month' },
        { label: 'This Year', value: 'year' }
      ],
      // 添加展开/收起状态变量
      isStocksExpanded: false,
      isNewsExpanded: false,
      // 初始显示数量
      initialStockCount: 6,
      initialNewsCount: 6,
      activeDateFilter: 0,
      // 控制新闻加载数量
      displayedCount: 8,
      itemsPerLoad: 8,
      // 添加股票分页控制变量
      displayedStockCount: 6,
      stocksPerLoad: 6,
      // 股票分页相关变量
      currentPage: 1,
      stocksPerPage: 6,
      // 新增新闻分页相关变量
      currentNewsPage: 1,
      newsPerPage: 8
    };
  },
  async created() {
    try {
      console.log("Request Data")
      // 并行获取所有数据
      const [stocksRes, newsRes, weatherRes] = await Promise.all([
        axios.get('/api/data/stocks'),
        axios.get('/api/data/news'),
        axios.get('/api/data/weather')
      ]);

      this.stocks = stocksRes.data;
      this.newsItems = newsRes.data;
      this.weather = weatherRes.data;
      console.log(this.stocks)
    } catch (error) {
      console.log('Failed to fetch data:', error);
      // 可以添加错误处理逻辑
    }
  },
  computed: {
    // 格式化更新日期
    formattedUpdateDate() {
      try {
        // 获取当前日期和时间
        const today = new Date();

        // 定义日期格式化选项
        const options = {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        };
        // 格式化并返回当前日期
        return today.toLocaleDateString('en-US', options);
      } catch (error) {
        console.error('Failed to format current date:', error);
        return 'Invalid date';
      }
    },

    // 筛选后的新闻列表
    filteredNews() {
      let result = [...this.newsItems];

      // 类别筛选
      if (this.activeTab !== 0) {
        const category = this.newsTabs[this.activeTab];
        result = result.filter(news => news.Category === category);
      }

      // 日期筛选
      result = this.filterByDate(result);
      return result;
    },

    // 筛选后的新闻列表
    filteredNews() {
      let result = [...this.newsItems];

      // 类别筛选
      if (this.activeTab !== 0) {
        const category = this.newsTabs[this.activeTab];
        result = result.filter(news => news.Category === category);
      }

      // 日期筛选
      result = this.filterByDate(result);
      return result;
    },

    // 修改新闻显示计算属性为分页模式
    displayedNews() {
      const startIndex = (this.currentNewsPage - 1) * this.newsPerPage;
      const endIndex = startIndex + this.newsPerPage;
      return this.filteredNews.slice(startIndex, endIndex);
    },

    // 计算新闻总页数
    totalNewsPages() {
      return Math.ceil(this.filteredNews.length / this.newsPerPage);
    },

    // 生成新闻页码数组 - 只显示3个页码
    newsPageNumbers() {
      const pages = [];
      const totalPages = this.totalNewsPages;
      const current = this.currentNewsPage;

      // 当总页数小于等于3时，显示所有页码
      if (totalPages <= 3) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // 显示当前页和前后各一页
        if (current === 1) {
          pages.push(1, 2, 3);
        } else if (current === totalPages) {
          pages.push(totalPages - 2, totalPages - 1, totalPages);
        } else {
          pages.push(current - 1, current, current + 1);
        }
      }

      return pages;
    },

    // 是否还有更多新闻可以加载 - 保留但可能不再使用
    hasMoreNews() {
      return this.displayedCount < this.filteredNews.length;
    },

    // 股票显示计算属性
    displayedStocks() {
      const startIndex = (this.currentPage - 1) * this.stocksPerPage;
      const endIndex = startIndex + this.stocksPerPage;
      return this.stocks.slice(startIndex, endIndex);
    },
    // 计算股票总页数
    totalStockPages() {
      return Math.ceil(this.stocks.length / this.stocksPerPage);
    },
    // 生成股票页码数组 - 只显示3个页码
    pageNumbers() {
      const pages = [];
      const totalPages = this.totalStockPages;
      const current = this.currentPage;

      // 当总页数小于等于3时，显示所有页码
      if (totalPages <= 3) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // 显示当前页和前后各一页
        if (current === 1) {
          pages.push(1, 2, 3);
        } else if (current === totalPages) {
          pages.push(totalPages - 2, totalPages - 1, totalPages);
        } else {
          pages.push(current - 1, current, current + 1);
        }
      }

      return pages;
    }
  },
  methods: {
    // 滚动到指定区域
    scrollToSection(sectionId) {
      this.activeSection = sectionId;
      const element = this.$refs[`${sectionId}Section`];
      if (element) {
        window.scrollTo({
          top: element.offsetTop - 100,
          behavior: 'smooth'
        });
      }
    },
    // 监听滚动，更新活跃区域
    handleScroll() {
      const scrollPosition = window.scrollY + 150;

      const weatherTop = this.$refs.weatherSection?.offsetTop || 0;
      const stockTop = this.$refs.stockSection?.offsetTop || 0;
      const newsTop = this.$refs.newsSection?.offsetTop || 0;

      if (scrollPosition < stockTop) {
        this.activeSection = 'weather';
      } else if (scrollPosition < newsTop) {
        this.activeSection = 'stocks';
      } else {
        this.activeSection = 'news';
      }
    },
    // 按日期筛选新闻
    filterByDate(newsItems) {
      const today = new Date();
      const filterType = this.dateFilters[this.activeDateFilter].value;

      let filteredItems = newsItems.filter(item => {
        // 转换新闻日期格式
        const [datePart, timePart] = item.PubData.split(' ');
        const newsDate = new Date(datePart);

        switch (filterType) {
          case 'yesterday':
            // 昨天：新闻日期的年月日等于今天的前一天
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            return newsDate.getFullYear() === yesterday.getFullYear() &&
                   newsDate.getMonth() === yesterday.getMonth() &&
                   newsDate.getDate() === yesterday.getDate();
          
          case 'week':
            // 计算本周的第一天（周一）
            const firstDayOfWeek = new Date(today);
            firstDayOfWeek.setDate(today.getDate() - (today.getDay() === 0 ? 6 : today.getDay() - 1));
            // 设置为当天的开始时间
            firstDayOfWeek.setHours(0, 0, 0, 0);
            
            // 计算本周的最后一天（周日）
            const lastDayOfWeek = new Date(firstDayOfWeek);
            lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
            // 设置为当天的结束时间
            lastDayOfWeek.setHours(23, 59, 59, 999);
            
            return newsDate >= firstDayOfWeek && newsDate <= lastDayOfWeek;
          
          case 'month':
            // 本月：新闻日期的年份和月份等于今天的年份和月份
            return newsDate.getFullYear() === today.getFullYear() &&
                   newsDate.getMonth() === today.getMonth();
          
          case 'year':
            // 本年：新闻日期的年份等于今天的年份
            return newsDate.getFullYear() === today.getFullYear();
          
          default: // 'all'
            return true;
        }
      });

      // 对所有标签下的新闻都按日期从大到小排序
      filteredItems.sort((a, b) => {
        const dateA = new Date(a.PubData.split(' ')[0]);
        const dateB = new Date(b.PubData.split(' ')[0]);
        return dateB - dateA; // 降序排列
      });

      return filteredItems;
    },

    // 删除原有的新闻加载更多和收起方法
    // 添加新闻分页相关方法
    goToNewsPage(page) {
      if (page >= 1 && page <= this.totalNewsPages) {
        this.currentNewsPage = page;
        // 更新输入框的值
        this.jumpToPage = page;
      }
    },
    prevNewsPage() {
      if (this.currentNewsPage > 1) {
        this.currentNewsPage--;
        // 更新输入框的值
        this.jumpToPage = this.currentNewsPage;
      }
    },
    nextNewsPage() {
      if (this.currentNewsPage < this.totalNewsPages) {
        this.currentNewsPage++;
        // 更新输入框的值
        this.jumpToPage = this.currentNewsPage;
      }
    },
    // 添加页码跳转方法
    jumpToNewsPage() {
      let page = parseInt(this.jumpToPage);
      // 验证页码有效性
      if (isNaN(page) || page < 1 || page > this.totalNewsPages) {
        // 重置为当前页码
        this.jumpToPage = this.currentNewsPage;
        return;
      }
      this.goToNewsPage(page);
    },
    // 股票分页相关方法
    goToPage(page) {
      if (page >= 1 && page <= this.totalStockPages) {
        this.currentPage = page;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalStockPages) {
        this.currentPage++;
      }
    },
    // 添加getChangeClass方法
    getChangeClass(stock) {
      if (!stock || !stock.change) {
        return '';
      }
      return stock.direction === "Down" ? 'positive' : 'negative';
    }
  },
  watch: {
    // 当筛选条件变化时重置新闻页码
    activeTab() {
      this.currentNewsPage = 1;
    },
    activeDateFilter() {
      this.currentNewsPage = 1;
    }
  },
  mounted() {
    // 监听滚动事件
    window.addEventListener('scroll', this.handleScroll);

    // 添加页面载入动画
    setTimeout(() => {
      document.querySelectorAll('.tool-card').forEach(card => {
        card.classList.add('visible');
      });
    }, 100);
  },
  beforeUnmount() {
    // 移除滚动监听
    window.removeEventListener('scroll', this.handleScroll);
  }
};
</script>

<style scoped>
/* 基础样式 */
.pharma-daily-container {
  background: linear-gradient(180deg, #f0f4f8 0%, #ffffff 100%);
  min-height: 100vh;
  padding: 0 15px;
  background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%239C92AC' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E");
}

/* 分页控件样式 */
.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 4px;
  /* 减小间距 */
}

.pagination-btn {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;
  padding: 8px 14px;
  /* 增加内边距 */
  border-radius: 8px;
  /* 增大圆角 */
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  /* 加粗文字 */
}

.pagination-btn:hover:not(.active):not(:disabled) {
  background-color: #e2e8f0;
  color: #334155;
  transform: translateY(-1px);
  /* 轻微上浮效果 */
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  /* 添加阴影 */
}

.pagination-btn.active {
  background-color: #3b82f6;
  color: white;
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  /* 添加蓝色阴影 */
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 添加页码跳转样式 */
.page-jump {
  display: inline-flex;
  align-items: center;
  margin: 0 10px;
}

.page-input {
  width: 50px;
  height: 30px;
  margin: 0 5px;
  padding: 0 5px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
}

.jump-btn {
  height: 30px;
  padding: 0 10px;
  background-color: #4a6cf7;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.jump-btn:hover {
  background-color: #3a5ae7;
}

/* 导航栏样式 */
.main-nav {
  background-color: #0f172a;
  padding: 0.5rem 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: sticky;
  top: 0;
  z-index: 100;
}

.nav-container {
  max-width: 100%;
  width: calc(100% - 30px);
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 1.2rem;
}

.nav-links {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

.nav-link {
  color: #e2e8f0;
  /* 更亮的导航文字 */
  background: none;
  border: none;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 1rem;
  position: relative;
  transition: all 0.3s ease;
  border-radius: 6px;
}

.nav-link:hover {
  color: #e2e8f0;
  background-color: rgba(255, 255, 255, 0.05);
}

.nav-link.active {
  color: #ffffff;
  /* 活动状态文字为白色 */
  background-color: rgba(59, 130, 246, 0.1);
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: #3b82f6;
  border-radius: 3px 3px 0 0;
}

/* 简介部分样式 */
.intro-section {
  padding: 2rem 0;
  /* 调整内边距以匹配图片 */
  background: linear-gradient(to right, rgba(22, 93, 255, 0.1), rgba(54, 179, 126, 0.1));
  /* 水平渐变背景 */
  border-radius: 12px;
  /* 添加圆角 */
  margin: 18px auto;
  /* 添加外边距 */
  max-width: 1600px;
  /* 设置最大宽度 */
  position: relative;
  overflow: hidden;
}

.intro-section::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 60%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  overflow: hidden;
}

.intro-section::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #3b82f6, #10b981, #f59e0b);
}


.intro-content {
  max-width: 100%;
  width: calc(100% - 30px);
  margin: 0 auto;
  position: relative;
}

.intro-header {
  display: flex;
  /* 使用flex布局 */
  justify-content: space-between;
  /* 两端对齐 */
  align-items: center;
  /* 垂直居中对齐 */
  padding: 0 20px;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 2rem;
}

.intro-title-group {
  flex: 1;
}

.update-date-container {
  margin-left: 20px;
  /* 右侧日期容器的左边距 */
  min-width: 200px;
  /* 设置最小宽度 */
}

.title-decoration {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.6s forwards 0.2s;
}

.decoration-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #94a3b8, transparent);
}

.title-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.6s forwards 0.4s;
}

.main-title {
  background: linear-gradient(90deg, #0f172a, #3b82f6);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  line-height: 1.1;
  position: relative;
}

.main-title::after {
  content: '';
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 12px;
  background-color: rgba(247, 245, 245, 0.1);
  z-index: -1;
  transform: skewY(-1deg);
}

.sub-title {
  color: #ffffff;
  font-size: clamp(1.25rem, 2vw, 1.75rem);
  font-weight: 600;
  margin-top: -0.5rem;
}

.intro-description {
  color: #c9ced4;
  /* 加深描述文字颜色，提高对比度 */
  font-size: 1.2rem;
  line-height: 1.6;
  max-width: 800px;
  margin: 0;
  padding: 0.5rem 0;
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.6s forwards 0.6s;
}

.update-date-container {
  background-color: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.07);
  align-self: flex-start;
  margin-top: 1rem;
  border-left: 4px solid #3b82f6;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 280px;
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.6s forwards 0.6s;
}

.date-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(59, 130, 246, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 1.5rem;
}

.update-date-container p {
  margin: 0;
  color: #64748b;
  font-weight: 500;
  font-size: 0.9rem;
}

.update-date-container span {
  color: #0f172a;
  font-weight: 600;
  font-size: 1rem;
  display: block;
  margin-top: 0.25rem;
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.intro-title-group {
  flex: 1;
  min-width: 300px;
}

/* 主内容容器 */
.content-container {
  max-width: 1500px;
  /* 与简介部分保持一致 */
  margin: 0 auto;
  /* 水平居中 */
  padding: 0 15px;
  /* 添加左右内边距，防止内容贴边 */
}

/* 网格布局 */
.tools-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  max-width: 1500px;
  /* 确保网格容器也遵循相同的最大宽度 */
  margin: 0 auto;
  /* 水平居中 */
}

/* 卡片头部样式 - 图标和标题同一行 */
.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

/* 卡片样式 */
.tool-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  opacity: 0;
  transform: translateY(20px);
  border: none;
  overflow: hidden;
  position: relative;
}

.tool-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
}

.weather-card::before {
  background: linear-gradient(90deg, #3b82f6, #2563eb);
}

.stock-card::before {
  background: linear-gradient(90deg, #10b981, #059669);
}

.news-card::before {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.tool-card.visible {
  opacity: 1;
  transform: translateY(0);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
}

.tool-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
}

.full-width {
  width: 100%;
}

/* 图标样式 */
.tool-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.tool-card:hover .tool-icon {
  transform: scale(1.1);
}

.weather-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.stock-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.news-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

/* 卡片内容容器 */
.card-content {
  margin-top: 0;
}

.tool-card h3 {
  color: #0f172a;
  font-size: 1.9rem;
  margin: 0;
  font-weight: 700;
  padding-bottom: 0;
  border-bottom: none;
  position: relative;
  padding-left: 0.5rem;
}

.tool-card h3::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  border-radius: 2px;
}

.weather-card h3::before {
  background-color: #3b82f6;
}

.stock-card h3::before {
  background-color: #10b981;
}

.news-card h3::before {
  background-color: #f59e0b;
}

/* 天气卡片样式 */
.weather-details {
  margin-top: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
}

.weather-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 0;
  flex: 1;
  min-width: 150px;
}

.weather-item i {
  color: #3b82f6;
  font-size: 1.5rem;
  margin-right: 1rem;
  width: 28px;
  text-align: center;
}

.weather-item .label {
  color: #64748b;
  font-size: 1.2rem;
  margin: 0;
}

.weather-item .value {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0.25rem 0 0 0;
  color: #0f172a;
}

.weather-tip {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f0f9ff;
  border-left: 4px solid #3b82f6;
  border-radius: 0 8px 8px 0;
  display: flex;
  align-items: center;
  flex-basis: 100%;
}

.weather-tip i {
  color: #3b82f6;
  margin-right: 0.75rem;
  font-size: 1.2rem;
}

.weather-tip p {
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
  font-weight: 500;
}

/* 股票卡片样式 */
.stock-table-container {
  margin-top: 1.5rem;
  overflow-x: auto;
}

.stock-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 1.1rem;
}

.stock-table th {
  text-align: center;
  padding: 1.2rem 0.75rem;
  color: #64748b;
  font-weight: 600;
  border-bottom: 2px solid #f1f5f9;
}

.stock-table td {
  padding: 1.2rem 0.75rem;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.2s ease;
  color: #0f172a;
}

.stock-row {
  transition: all 0.2s ease;
}

.stock-row:hover {
  background-color: #f0f9ff;
  transform: translateX(5px);
}

.positive {
  color: #10b981;
  font-weight: 600;
}

.negative {
  color: #ef4444;
  font-weight: 600;
}

/* 新闻部分样式 */
/* 新闻分类标签 */
.news-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.news-tabs .tab-btn {
  padding: 0.5rem 1.25rem;
  border-radius: 20px;
  background-color: #f1f5f9;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.0rem;
}


.news-tabs .tab-btn.active {
  background-color: #3b82f6;
  color: white;
}

.news-tabs::-webkit-scrollbar {
  height: 4px;
}

.news-tabs::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 4px;
}

/* 日期筛选标签样式 */
.date-tabs {
  display: flex;
  overflow-x: auto;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  scrollbar-width: thin;
  padding-top: 0.5rem;
}


.date-tabs .tab-btn {
  padding: 0.5rem 1.25rem;
  border-radius: 20px;
  background-color: #f1f5f9;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.0rem;
}

.date-tabs .tab-btn.active {
  background-color: #10b981;
  color: white;
}

.date-tabs::-webkit-scrollbar {
  height: 4px;
}

.date-tabs::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 4px;
}

.tab-btn {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background-color: white;
  color: #64748b;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.tab-btn.active {
  background-color: #36b37e;
  color: white;
}

/* 新闻卡片网格布局 */
.news-cards-grid {
  display: grid;
  background-color: #f9fafb;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

/* 新闻卡片样式 */
.news-card-item {
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease;
  border: 1px solid #f8fafc;
  position: relative;
  overflow: hidden;
  align-items: flex-start;
}

.news-card-item::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background-color: #3b82f6;
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.news-card-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.08);
  border-color: transparent;
}

.news-card-item:hover::after {
  transform: scaleY(1);
}

.news-category {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: #e7eeff;
  color: rgb(22 93 255);
  font-size: 1.0rem;
  font-weight: 600;
  border-radius: 999px;
  margin-bottom: 0.75rem;
  text-align: left;
}

.news-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #0f172a;
  line-height: 1.5;
  transition: color 0.3s ease;
  text-align: left;
}

.news-card-item:hover .news-title {
  color: #3b82f6;
}

.news-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.news-date {
  color: #718096;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.news-date::before {
  content: "🕒";
  display: inline-block;
}

.read-more {
  color: #3b82f6;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: color 0.2s ease;
}

.read-more:hover {
  color: #2563eb;
  text-decoration: underline;
}

.read-more i {
  margin-left: 0.25rem;
}

/* 加载更多按钮 */
.load-more-container {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  /* 添加上边距以增加间隔 */
}

.load-more-btn {
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.load-more-btn:hover {
  background-color: #2563eb;
  transform: translateY(-2px);
}

.cta {
  position: relative;
  margin: auto;
  padding: 12px 18px;
  transition: all 0.2s ease;
  border: none;
  background: none;
  cursor: pointer;
}

.cta:before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  border-radius: 50px;
  background: #b1dae7;
  width: 45px;
  height: 45px;
  transition: all 0.3s ease;
}

.cta span {
  position: relative;
  font-family: "Ubuntu", sans-serif;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #234567;
}

.cta svg {
  position: relative;
  top: 0;
  margin-left: 10px;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke: #234567;
  stroke-width: 2;
  transform: translateX(-5px);
  /* transition: all 0.3s ease; */
  transition: transform 0.3s ease;
}

.cta:hover:before {
  width: 100%;
  background: #b1dae7;
}

.cta:hover svg {
  transform: translateX(0);
}

.cta:active {
  transform: scale(0.95);
}

/* 响应式调整 */
@media (max-width: 768px) {
  .nav-container {
    flex-direction: column;
    height: auto;
    padding: 1rem;
    gap: 1rem;
  }

  .nav-links {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .intro-header {
    flex-direction: column;
  }

  .update-date-container {
    align-self: stretch;
    margin-top: 0;
    padding: 1rem;
    border-radius: 12px;
  }

  .weather-details {
    flex-direction: column;
    gap: 1rem;
  }

  .news-cards-grid {
    grid-template-columns: 1fr;
  }

  .tool-card {
    padding: 1.5rem;
    border-radius: 12px;
  }

  .nav-link {
    padding: 0.5rem 0.8rem;
    font-size: 1.2rem;
  }

  .main-title::after {
    height: 8px;
    bottom: 2px;
  }
}

@media (max-width: 480px) {

  .content-container,
  .intro-content {
    width: calc(100% - 20px);
  }

  .tool-card {
    padding: 1.25rem;
  }
}
</style>